import React, { useState } from 'react';
import { get, uniqueId } from 'lodash';
import { SdfBadge, SdfBox, SdfExpandableBox, SdfIcon } from '@synerg/react-components';

import { setLoadings, updateCurrentProjectDetails, useApplicationState } from '../../../utilities/applicationState';
import MarketplaceListing, { ApplicationIds, ProdCredentials } from './prodProjectDetailComponents';
import { EditPartnerProject } from './editPartnerProject';
import { capitalizeFirstLetter } from '../../common/util';
import Toast from '../../common/toast';

export default function ProdProjectDetails({ projectListData, getClientSecret, submitAppListingFields }) {

	const [store, dispatch] = useApplicationState();
	const { loadings, projects: { current } } = store;
	const { devInfo, prodInfo, projectCarDetails = {}, prodb2bClientId, appListingSubmitStatus = [], appListingUrlNumber = [], prodb2cClientId, prodb2bClientSecret, prodb2cClientSecret, isAppListingEditMode = false, showProdB2bSecret = false, showProdB2cSecret = false } = current;
	const { isLoadingAppListing = false, isLoadingB2cSecret = false, isLoadingB2bSecret = false } = loadings;
	const isProdB2bClientId = prodb2bClientId && prodb2bClientId.toLowerCase() !== 'not available';
	const isProdB2cClientId = prodb2cClientId && prodb2cClientId.toLowerCase() !== 'not available';
	const isProdB2bAndB2cClientIds = !isProdB2bClientId && !isProdB2cClientId ? false : true;
	const prodDataCloudProject = get(prodInfo, 'platform', '') === 'datacloud';
	const redirecrUrl = !prodDataCloudProject && get(projectCarDetails, 'prod.b2cRedirectLink.href', '');
	const associatedProject = {
		b2bConsumerAppID: devInfo.b2bConsumerAppID || '',
		b2cConsumerAppID: devInfo.b2cConsumerAppID || ''
	};
	const projectDetail = { ...prodInfo, associatedProject };
	const [isInfoOpen, setIsInfoOpen] = useState(true);
	const [showEditModal, setShowEditModal] = useState(false);
	const [toast, setToast] = useState(false);

	const STATE = {
		LIVE: 'complete',
		NOT_LIVE: 'changed',
		SUSPENDED: 'failed'
	};

	let projectStatus = 'LIVE';
	if (prodInfo.status) {
		projectStatus = !prodDataCloudProject ? prodInfo.status : 'LIVE';
	}

	const handleResponse = (message, status = "error") => {
		setToast({
			key: uniqueId(),
			status: status,
			message: message,
			showToast: true
		});
	};

	const prodToggleShowSecret = (toggled, clientType) => {
		if (clientType === 'B2b') {
			dispatch(updateCurrentProjectDetails({ showProdB2bSecret: toggled }));

			if (toggled === false) {
				dispatch(updateCurrentProjectDetails({ prodb2bClientSecret: null }));

			} else if (toggled === true && prodInfo._id) {
				getProdSecrets(clientType);
			}
		}

		if (clientType === 'B2c') {
			dispatch(updateCurrentProjectDetails({ showProdB2cSecret: toggled }));
			if (toggled === false) {
				dispatch(updateCurrentProjectDetails({ prodb2cClientSecret: null }));

			} else if (toggled === true && prodInfo._id) {
				getProdSecrets(clientType);
			}
		}
	};

	const getProdSecrets = (clientType) => {
		// Client Credentials
		if (clientType === 'B2b') {
			if (prodb2bClientId !== 'Not available') {
				dispatch(setLoadings({ isLoadingB2bSecret: true }));

				getClientSecret(prodInfo.b2bConsumerAppID, prodInfo._id)
					.then(b2bs => {
						dispatch(updateCurrentProjectDetails({ prodb2bClientSecret: b2bs }));
					})
					.catch(() => {
						dispatch(updateCurrentProjectDetails({ prodb2bClientSecret: null }));
					})
					.finally(() => {
						dispatch(setLoadings({ isLoadingB2bSecret: false }));
					});
			}
		}
		// Authorization Code
		if (clientType === 'B2c') {
			if (prodb2cClientId !== 'Not available') {
				dispatch(setLoadings({ isLoadingB2cSecret: true }));
				getClientSecret(prodInfo.b2cConsumerAppID, prodInfo._id)
					.then(b2cs => {
						dispatch(updateCurrentProjectDetails({ prodb2cClientSecret: b2cs }));
					})
					.catch(() => {
						dispatch(updateCurrentProjectDetails({ prodb2cClientSecret: null }));
					})
					.finally(() => {
						dispatch(setLoadings({ isLoadingB2cSecret: false }));
					});
			}
		}
	};

	return (
		<div>
			{toast && <Toast
				key={toast.key}
				status={toast.status}
				message={toast.message}
				showToast={toast.showToast}
			/>}
			<div>
				<div className="text-heading-05 font-bold mt-4">Production details</div>
				<div className="mt-5">
					{showEditModal &&
						<EditPartnerProject
							projectDetail={projectDetail}
							showEditModal={showEditModal}
							setShowEditModal={setShowEditModal}
							handleResponse={handleResponse}
						/>
					}
					<SdfExpandableBox
						variant="bordered"
						triggerPlacement="after"
						stroke="light"
						opened={isInfoOpen}
						onSdfClose={() => setIsInfoOpen(false)}
						onSdfOpen={() => setIsInfoOpen(true)}
						className="project-info pb-8"
					>
						<div className="text-lg font-bold" slot="header" style={{ letterSpacing: '-0.7px' }}>
							Production info
						</div>
						<div className="text-sm font-medium mt-5 info-title">
							Project name
						</div>
						<div className="flex items-center col-gap-4">
							<div className="text-body-02 mt-2 info-content">
								{projectDetail.name}
								<SdfIcon icon="action-edit" className="ml-2 cursor-pointer" onClick={() => setShowEditModal(true)} />
							</div>
							<SdfBadge status={STATE[projectStatus]}>
								{capitalizeFirstLetter(projectStatus.split('_').join(' '))}
							</SdfBadge>
						</div>

						<div className="text-sm font-medium mt-6 info-title">
							Description
						</div>

						<div className="text-body-02 mt-2 info-content">
							{!projectListData.legacy && projectDetail.description ? projectDetail.description : 'NA' }
						</div>
					</SdfExpandableBox>

					{isProdB2bAndB2cClientIds && <div className="mt-16 mb-5">
						<div className="text-heading-05 font-bold">Production credentials</div>

						{prodInfo.dataConnectorID && isProdB2bClientId && <ProdCredentials clientType={"B2b"} prodClientId={prodb2bClientId} prodClientSecret={prodb2bClientSecret} showSecret={showProdB2bSecret} prodToggleShowSecret={prodToggleShowSecret} clientSecretLoading={isLoadingB2bSecret} step={0} />}

						{prodInfo.endUserAppID && isProdB2cClientId && <ProdCredentials clientType={"B2c"} prodClientId={prodb2cClientId} prodClientSecret={prodb2cClientSecret} showSecret={showProdB2cSecret} prodToggleShowSecret={prodToggleShowSecret} clientSecretLoading={isLoadingB2cSecret} step={1} />}

					</div>}

					{(prodInfo.dataConnectorID !== 'Not available' || prodInfo.endUserAppID !== 'Not available') && <div className="mt-6">
						<div className="text-heading-05 font-bold">Application IDs</div>
						{prodInfo.dataConnectorID !== 'Not available' && <ApplicationIds appType="dataConnector" appId={prodInfo.dataConnectorID} />}

						{prodInfo.endUserAppID !== 'Not available' && <ApplicationIds appType="endUser" appId={prodInfo.endUserAppID} />}
					</div>}

					{redirecrUrl && <div className="mt-16">
						<div className="text-heading-05 font-bold">Redirect URL</div>
						<SdfBox variant="bordered" className="mt-5 py-4">
							<div className="my-4">
								<div className="text-heading-02 font-bold">URL</div>
								<div className="text-body-02 mt-2">{redirecrUrl}</div>
							</div>
						</SdfBox>
					</div>}

					{!prodDataCloudProject && <div>
						<div className="text-heading-05 font-bold mt-16">Marketplace listing</div>
						<MarketplaceListing
							editMode={isAppListingEditMode}
							submit={{
								onSubmit: submitAppListingFields,
								isSaving: isLoadingAppListing,
								status: appListingSubmitStatus,
								numberOfUrls: appListingUrlNumber
							}}
						/>
					</div>}
				</div>
			</div>
		</div>
	)
};
